// Alton Valley — project plan scheduling engine
//
// One place where a plan is turned into dates. Everything that used to
// recompute dates in the browser (the SOW Plan tab and the Week Planner each
// had their own copy of a cascade loop) now calls this.
//
// What it does, in order:
//
//   1. A working calendar: weekends off, bank holidays off, company shutdown
//      days off. Durations can opt out of it ("plaster dries at the weekend").
//   2. A forward pass over the dependency graph: FS, SS, FF and SF links, any
//      number of predecessors per task, signed lag in working or calendar days,
//      and scheduling constraints (ASAP, start no earlier than, must start on,
//      finish no later than).
//   3. Roll-up of summary (WBS) rows from their children.
//   4. A backward pass for total float and the critical path.
//
// Two conventions worth stating because the old code got both wrong:
//
//   • Finish dates are INCLUSIVE. A one-day task starting Monday finishes
//     Monday. The previous helper added N working days to the start, so every
//     finish in the system read one working day late.
//   • A zero-duration task is a milestone. Its start and finish are the same
//     day, and it is drawn as a diamond rather than a one-day bar.
//
// No Node or Worker APIs are used, so the same code runs on the edge and under
// `node --test`.

/* ────────────────────────────────────────────────────────────
   Date primitives — plain YYYY-MM-DD strings, UTC arithmetic
   so a deploy region can never shift a plan by a day.
   ──────────────────────────────────────────────────────────── */

const MS_DAY = 86400000;

export function toUTC(dateStr) {
  const [y, m, d] = String(dateStr).slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

export function toISO(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

export function addCalendarDays(dateStr, n) {
  if (!dateStr) return null;
  return toISO(toUTC(dateStr) + Math.round(n) * MS_DAY);
}

/** Signed whole-day difference, b - a. */
export function calendarDaysBetween(a, b) {
  if (!a || !b) return null;
  return Math.round((toUTC(b) - toUTC(a)) / MS_DAY);
}

/** 0 = Sunday … 6 = Saturday, in UTC. */
export function dayOfWeek(dateStr) {
  return new Date(toUTC(dateStr)).getUTCDay();
}

/* ────────────────────────────────────────────────────────────
   Working calendar
   ──────────────────────────────────────────────────────────── */

const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5]; // Monday to Friday

/**
 * Build a calendar.
 *
 * @param {object}   opts
 * @param {string[]} opts.holidays     YYYY-MM-DD non-working dates (bank holidays,
 *                                     company shutdown days).
 * @param {number[]} opts.workingDays  Day-of-week numbers that are working days.
 */
export function makeCalendar({ holidays = [], workingDays = DEFAULT_WORKING_DAYS } = {}) {
  const holidaySet = new Set((holidays || []).map(h => String(h).slice(0, 10)));
  const workingSet = new Set(workingDays);

  // A calendar with no working days at all would spin forever in the loops
  // below. Fall back rather than hang a request.
  const working = workingSet.size ? workingSet : new Set(DEFAULT_WORKING_DAYS);

  const isWorkingDay = (dateStr) =>
    !!dateStr && working.has(dayOfWeek(dateStr)) && !holidaySet.has(String(dateStr).slice(0, 10));

  // Guard every scan: 20 years of non-working days is not a plan, it is a bug.
  const SCAN_LIMIT = 7300;

  const nextWorkingDay = (dateStr) => {
    let d = dateStr, i = 0;
    while (!isWorkingDay(d) && i++ < SCAN_LIMIT) d = addCalendarDays(d, 1);
    return d;
  };

  const prevWorkingDay = (dateStr) => {
    let d = dateStr, i = 0;
    while (!isWorkingDay(d) && i++ < SCAN_LIMIT) d = addCalendarDays(d, -1);
    return d;
  };

  /** Move n working days from dateStr. n may be negative. n = 0 snaps forward. */
  const addWorkingDays = (dateStr, n) => {
    if (!dateStr) return null;
    let d = nextWorkingDay(dateStr);
    let remaining = Math.round(n);
    const step = remaining < 0 ? -1 : 1;
    let i = 0;
    while (remaining !== 0 && i++ < SCAN_LIMIT) {
      d = addCalendarDays(d, step);
      if (isWorkingDay(d)) remaining -= step;
    }
    return d;
  };

  /** Inclusive count of working days from a to b. Same day = 1. */
  const workingDaysBetween = (a, b) => {
    if (!a || !b) return null;
    const sign = toUTC(b) < toUTC(a) ? -1 : 1;
    const [from, to] = sign === 1 ? [a, b] : [b, a];
    let d = from, count = 0, i = 0;
    while (toUTC(d) <= toUTC(to) && i++ < SCAN_LIMIT) {
      if (isWorkingDay(d)) count++;
      d = addCalendarDays(d, 1);
    }
    return count * sign;
  };

  /**
   * Inclusive finish for a task starting on `start`.
   * Duration 0 (or less) is a milestone: finish equals start.
   */
  const finishFromStart = (start, duration, unit = 'workdays') => {
    if (!start) return null;
    const d = Number(duration) || 0;
    if (d <= 0) return unit === 'calendar' ? start : nextWorkingDay(start);
    const whole = Math.max(1, Math.ceil(d));
    if (unit === 'calendar') return addCalendarDays(start, whole - 1);
    return addWorkingDays(nextWorkingDay(start), whole - 1);
  };

  /** Inverse of finishFromStart. */
  const startFromFinish = (finish, duration, unit = 'workdays') => {
    if (!finish) return null;
    const d = Number(duration) || 0;
    if (d <= 0) return unit === 'calendar' ? finish : prevWorkingDay(finish);
    const whole = Math.max(1, Math.ceil(d));
    if (unit === 'calendar') return addCalendarDays(finish, -(whole - 1));
    return addWorkingDays(prevWorkingDay(finish), -(whole - 1));
  };

  /** Shift a date by a lag, honouring the lag's own unit. */
  const applyLag = (dateStr, lag, unit = 'workdays') => {
    const n = Number(lag) || 0;
    if (!dateStr || n === 0) return dateStr;
    return unit === 'calendar' ? addCalendarDays(dateStr, n) : addWorkingDays(dateStr, n);
  };

  return {
    isWorkingDay, nextWorkingDay, prevWorkingDay, addWorkingDays,
    workingDaysBetween, finishFromStart, startFromFinish, applyLag,
    holidays: holidaySet, workingDays: working,
  };
}

/* ────────────────────────────────────────────────────────────
   Errors
   ──────────────────────────────────────────────────────────── */

export class ScheduleCycleError extends Error {
  constructor(taskIds) {
    super('The plan contains a circular dependency, so it cannot be scheduled.');
    this.name = 'ScheduleCycleError';
    this.taskIds = taskIds;
  }
}

/* ────────────────────────────────────────────────────────────
   Comparison helpers
   ──────────────────────────────────────────────────────────── */

const laterOf   = (a, b) => (!a ? b : !b ? a : (toUTC(a) >= toUTC(b) ? a : b));
const earlierOf = (a, b) => (!a ? b : !b ? a : (toUTC(a) <= toUTC(b) ? a : b));

export const DEP_TYPES = ['FS', 'SS', 'FF', 'SF'];
export const CONSTRAINT_TYPES = ['asap', 'snet', 'fnlt', 'mso'];

/* ────────────────────────────────────────────────────────────
   Graph construction and ordering
   ──────────────────────────────────────────────────────────── */

/**
 * Topological order over two kinds of edge:
 *   • predecessor → successor (a dependency)
 *   • child → parent          (a summary cannot be known before its children)
 *
 * Doing both in one sort means "a task depends on its own summary bar" is
 * caught by the same cycle detection as any other loop.
 */
export function topoOrder(tasks, deps) {
  const byId = new Map(tasks.map(t => [t.id, t]));
  const indegree = new Map(tasks.map(t => [t.id, 0]));
  const out = new Map(tasks.map(t => [t.id, []]));

  const edge = (from, to) => {
    if (!byId.has(from) || !byId.has(to) || from === to) return;
    out.get(from).push(to);
    indegree.set(to, indegree.get(to) + 1);
  };

  for (const d of deps) edge(d.predecessor_id, d.task_id);
  for (const t of tasks) if (t.parent_task_id) edge(t.id, t.parent_task_id);

  // Seed in the caller's order so an unconstrained plan keeps the order the
  // user arranged rather than an arbitrary map order.
  const queue = tasks.filter(t => indegree.get(t.id) === 0).map(t => t.id);
  const order = [];
  while (queue.length) {
    const id = queue.shift();
    order.push(id);
    for (const next of out.get(id)) {
      indegree.set(next, indegree.get(next) - 1);
      if (indegree.get(next) === 0) queue.push(next);
    }
  }

  if (order.length !== tasks.length) {
    throw new ScheduleCycleError(tasks.map(t => t.id).filter(id => !order.includes(id)));
  }
  return order;
}

/* ────────────────────────────────────────────────────────────
   The forward pass
   ──────────────────────────────────────────────────────────── */

/**
 * Earliest start a single dependency allows.
 * Returns { start, finish } — whichever end the link actually pins.
 */
function constraintFromDep(cal, dep, pred, task) {
  const type = DEP_TYPES.includes(dep.dep_type) ? dep.dep_type : 'FS';
  const lag  = Number(dep.lag_days) || 0;
  const unit = dep.lag_unit === 'calendar' ? 'calendar' : 'workdays';

  if (!pred.start_date && !pred.end_date) return {};

  switch (type) {
    // Successor starts the working day after the predecessor finishes.
    case 'FS': {
      if (!pred.end_date) return {};
      const dayAfter = cal.addWorkingDays(pred.end_date, 1);
      return { start: cal.applyLag(dayAfter, lag, unit) };
    }
    // Successor starts no earlier than the predecessor started. This is the
    // one Owen's plastering example needs: SS + 2 weeks of drying.
    case 'SS': {
      if (!pred.start_date) return {};
      return { start: cal.applyLag(pred.start_date, lag, unit) };
    }
    // Successor finishes no earlier than the predecessor finishes.
    case 'FF': {
      if (!pred.end_date) return {};
      return { finish: cal.applyLag(pred.end_date, lag, unit) };
    }
    // Successor finishes no earlier than the predecessor starts.
    case 'SF': {
      if (!pred.start_date) return {};
      return { finish: cal.applyLag(pred.start_date, lag, unit) };
    }
    default:
      return {};
  }
}

/**
 * Schedule a plan.
 *
 * @param {object}   input
 * @param {object[]} input.tasks     Rows from sow_project_tasks. Mutated copies
 *                                   are returned; the input is left alone.
 * @param {object[]} input.deps      Rows from sow_task_dependencies.
 * @param {object}   input.calendar  From makeCalendar().
 * @param {string}   input.projectStart  Fallback start for a task with no
 *                                   predecessor, no constraint and no date yet.
 * @param {string}   input.today     YYYY-MM-DD, for progress and variance.
 *
 * @returns {{ tasks: object[], projectStart: string, projectFinish: string,
 *             warnings: object[] }}
 */
/** A working day in hours. Durations are days; work is hours; this converts. */
export const HOURS_PER_DAY = 7.5;

/**
 * Total resource units on a task: two people at full time is 2.0, one at half
 * time is 0.5. A task nobody is on is treated as one notional resource, so its
 * work still means something before it is staffed.
 */
function unitsFor(task, assignmentsByTask) {
  const list = assignmentsByTask.get(task.id) || [];
  // Nobody on it: the task's own share, which is what its team is asked for
  // (a part of a task split between teams carries less than 100%).
  if (!list.length) return Math.max(0.01, (Number(task.fte_pct) || 100) / 100);
  const total = list.reduce((s, a) => s + (Number(a.fte_pct) || 100) / 100, 0);
  return total > 0 ? total : 1;
}

/**
 * The work on a task when everybody on it has days of work set (stored as
 * planned hours): their sum. Null when anybody is on a share instead, so the
 * work is worked out from the duration as before.
 */
function plannedHoursFor(taskId, assignmentsByTask) {
  const list = assignmentsByTask.get(taskId) || [];
  if (!list.length || !list.every(a => Number(a.planned_hours) > 0)) return null;
  return list.reduce((s, a) => s + Number(a.planned_hours), 0);
}

export function schedulePlan({ tasks = [], deps = [], calendar, projectStart, today, assignments = [], hoursPerDay = HOURS_PER_DAY } = {}) {
  const cal = calendar || makeCalendar();
  const todayStr = today || toISO(Date.now());

  const assignmentsByTask = new Map();
  for (const a of assignments) {
    if (!assignmentsByTask.has(a.task_id)) assignmentsByTask.set(a.task_id, []);
    assignmentsByTask.get(a.task_id).push(a);
  }

  // Work on copies so a caller can diff old against new to decide what to save.
  const rows = tasks.map(t => ({ ...t }));
  const byId = new Map(rows.map(t => [t.id, t]));
  const warnings = [];

  // Drop links that point at nothing, rather than failing the whole plan.
  const links = deps.filter(d => byId.has(d.task_id) && byId.has(d.predecessor_id) && d.task_id !== d.predecessor_id);

  const childrenOf = new Map(rows.map(t => [t.id, []]));
  for (const t of rows) {
    if (t.parent_task_id && childrenOf.has(t.parent_task_id)) childrenOf.get(t.parent_task_id).push(t);
  }
  // A row with children is a summary whether or not anybody ticked the box.
  for (const t of rows) if (childrenOf.get(t.id).length) t.is_summary = 1;

  const depsBySuccessor = new Map();
  for (const d of links) {
    if (!depsBySuccessor.has(d.task_id)) depsBySuccessor.set(d.task_id, []);
    depsBySuccessor.get(d.task_id).push(d);
  }

  const order = topoOrder(rows, links);
  const fallbackStart = cal.nextWorkingDay(projectStart || todayStr);

  for (const id of order) {
    const t = byId.get(id);

    /* ── Summary rows: rolled up, never typed ── */
    if (t.is_summary) {
      // A summary's dates are its children's, so a predecessor on the summary
      // itself moves nothing. Saying so beats a link that looks live and is not.
      if ((depsBySuccessor.get(t.id) || []).length) {
        warnings.push({
          task_id: t.id, type: 'summary_predecessor',
          message: `"${t.name || 'Summary'}" is a summary bar, so its dates come from the tasks inside it and its own predecessors have no effect. Put the link on the first task underneath instead.`,
        });
      }
      const kids = childrenOf.get(t.id).filter(k => k.start_date || k.end_date);
      if (!kids.length) {
        t.start_date = null;
        t.end_date = null;
        t.duration_days = 0;
        t.completion_pct = 0;
        continue;
      }
      t.start_date = kids.map(k => k.start_date).filter(Boolean).reduce(earlierOf, null);
      t.end_date   = kids.map(k => k.end_date).filter(Boolean).reduce(laterOf, null);
      t.duration_days = t.start_date && t.end_date ? cal.workingDaysBetween(t.start_date, t.end_date) : 0;

      // Progress weighted by duration, so a two-week task counts for more than
      // an afternoon's work. Effort would be better still, and is used when
      // every child carries it.
      const weightOf = (k) => {
        const e = Number(k.effort_hours);
        return Number.isFinite(e) && e > 0 ? e : Math.max(0.5, Number(k.duration_days) || 0);
      };
      const allKids = childrenOf.get(t.id);
      const totalW = allKids.reduce((s, k) => s + weightOf(k), 0);
      t.completion_pct = totalW > 0
        ? Math.round(allKids.reduce((s, k) => s + weightOf(k) * effectivePct(k), 0) / totalW)
        : 0;
      t.effort_hours = allKids.reduce((s, k) => s + (Number(k.effort_hours) || 0), 0) || null;

      const kidActualStarts = allKids.map(k => k.actual_start).filter(Boolean);
      t.actual_start = kidActualStarts.length ? kidActualStarts.reduce(earlierOf, null) : null;
      const kidFinishes = allKids.map(k => k.actual_finish);
      t.actual_finish = kidFinishes.length && kidFinishes.every(Boolean)
        ? kidFinishes.reduce(laterOf, null)
        : null;
      continue;
    }

    /* ── Leaf tasks ── */
    const unit = t.duration_unit === 'calendar' ? 'calendar' : 'workdays';
    const isMilestone = !!t.is_milestone;

    /* The work / units / duration triangle.
       Effort driven: the work is fixed and the duration follows the number of
       people on it, so adding a second engineer to a five-day job halves it.
       Otherwise duration is fixed and the work follows, which is how a task
       with one owner has always behaved. */
    const units = unitsFor(t, assignmentsByTask);
    const plannedHours = plannedHoursFor(t.id, assignmentsByTask);
    t.assigned_units = Math.round(units * 100) / 100;
    let duration = isMilestone ? 0 : Math.max(0, Number(t.duration_days) || 0);

    if (isMilestone) {
      t.effort_hours = 0;
    } else if (t.effort_driven && Number(t.effort_hours) > 0) {
      // Rounded to the half day the rest of the plan is expressed in.
      const derived = Number(t.effort_hours) / (units * hoursPerDay);
      duration = Math.max(0.5, Math.round(derived * 2) / 2);
      t.duration_days = duration;
    } else if (plannedHours !== null) {
      // Days of work per person: the work is what they add up to.
      t.effort_hours = Math.round(plannedHours * 100) / 100;
    } else {
      t.effort_hours = Math.round(duration * units * hoursPerDay * 100) / 100;
    }

    /* Progress the booked time suggests, for comparison with the PM's own
       figure. Never overwrites it: a task can be 90% done on a fifth of the
       hours, and only the person doing it knows which. */
    const actual = Number(t.actual_work_hours);
    if (Number.isFinite(actual) && actual > 0 && Number(t.effort_hours) > 0) {
      t.suggested_pct = Math.min(100, Math.round((actual / Number(t.effort_hours)) * 100));
    } else {
      t.suggested_pct = null;
    }

    let earliestStart = null;   // from FS / SS links
    let earliestFinish = null;  // from FF / SF links

    for (const d of (depsBySuccessor.get(t.id) || [])) {
      const pred = byId.get(d.predecessor_id);
      const c = constraintFromDep(cal, d, pred, t);
      if (c.start)  earliestStart  = laterOf(earliestStart, c.start);
      if (c.finish) earliestFinish = laterOf(earliestFinish, c.finish);
    }

    // A finish-side link implies a start once the duration is known.
    if (earliestFinish) {
      const impliedStart = cal.startFromFinish(earliestFinish, duration, unit);
      earliestStart = laterOf(earliestStart, impliedStart);
    }

    const hasPredecessor = (depsBySuccessor.get(t.id) || []).length > 0;
    const constraintType = CONSTRAINT_TYPES.includes(t.constraint_type) ? t.constraint_type : 'asap';

    let start;
    if (t.actual_start) {
      // It has started. What was planned no longer matters for the start.
      start = t.actual_start;
    } else if (constraintType === 'mso' && t.constraint_date) {
      start = t.constraint_date;
      if (earliestStart && toUTC(earliestStart) > toUTC(start)) {
        warnings.push({
          task_id: t.id, type: 'constraint_conflict',
          message: `${t.name || 'Task'} must start on ${start} but its predecessors do not allow it to start before ${earliestStart}.`,
        });
      }
    } else if (hasPredecessor) {
      start = earliestStart || t.start_date || fallbackStart;
      if (constraintType === 'snet' && t.constraint_date) start = laterOf(start, t.constraint_date);
    } else {
      // No predecessors: the date is the planner's, not the engine's.
      start = t.start_date || t.constraint_date || fallbackStart;
      if (constraintType === 'snet' && t.constraint_date) start = laterOf(start, t.constraint_date);
    }

    if (unit !== 'calendar') start = cal.nextWorkingDay(start);

    let finish = t.actual_finish || cal.finishFromStart(start, duration, unit);
    if (isMilestone) finish = start;

    // A deadline flags, it never drags a task earlier than its logic allows.
    if (constraintType === 'fnlt' && t.constraint_date && toUTC(finish) > toUTC(t.constraint_date)) {
      warnings.push({
        task_id: t.id, type: 'deadline_missed',
        message: `${t.name || 'Task'} is forecast to finish ${finish}, past its ${t.constraint_date} deadline.`,
      });
    }

    t.start_date = start;
    t.end_date = finish;
    if (isMilestone) t.duration_days = 0;
  }

  const dated = rows.filter(t => t.start_date && t.end_date);
  const planStart  = dated.map(t => t.start_date).reduce(earlierOf, null);
  const planFinish = dated.map(t => t.end_date).reduce(laterOf, null);

  applyBackwardPass({ rows, byId, links, cal, order, planFinish, childrenOf });

  return { tasks: rows, projectStart: planStart, projectFinish: planFinish, warnings };
}

/** Percent complete a task should be credited with, whatever its status says. */
function effectivePct(t) {
  if (t.status === 'completed') return 100;
  if (t.status === 'n_a') return 100;
  const p = Number(t.completion_pct);
  return Number.isFinite(p) ? Math.min(100, Math.max(0, p)) : 0;
}

/* ────────────────────────────────────────────────────────────
   The backward pass — total float and the critical path
   ──────────────────────────────────────────────────────────── */

function applyBackwardPass({ rows, byId, links, cal, order, planFinish, childrenOf }) {
  if (!planFinish) {
    for (const t of rows) { t.total_float_days = null; t.is_critical = 0; }
    return;
  }

  const successorsOf = new Map(rows.map(t => [t.id, []]));
  for (const d of links) successorsOf.get(d.predecessor_id).push(d);

  const late = new Map(); // id → { ls, lf }

  // Reversed topological order visits every successor before its predecessor,
  // and (because child → parent is an edge) every parent before its children.
  // So a child can read the bound its summary bar has already been given.
  for (const id of [...order].reverse()) {
    const t = byId.get(id);
    if (!t.start_date || !t.end_date) { late.set(id, { ls: null, lf: null }); continue; }

    const unit = t.duration_unit === 'calendar' ? 'calendar' : 'workdays';
    const duration = t.is_milestone ? 0 : Math.max(0, Number(t.duration_days) || 0);

    let lf = null;
    for (const d of successorsOf.get(id)) {
      const succ = late.get(d.task_id);
      if (!succ || (!succ.ls && !succ.lf)) continue;
      const type = DEP_TYPES.includes(d.dep_type) ? d.dep_type : 'FS';
      const lag  = -(Number(d.lag_days) || 0);   // undo the forward lag
      const lunit = d.lag_unit === 'calendar' ? 'calendar' : 'workdays';

      let candidate = null;
      if (type === 'FS' && succ.ls) candidate = cal.addWorkingDays(cal.applyLag(succ.ls, lag, lunit), -1);
      else if (type === 'SS' && succ.ls) {
        candidate = cal.finishFromStart(cal.applyLag(succ.ls, lag, lunit), duration, unit);
      } else if (type === 'FF' && succ.lf) candidate = cal.applyLag(succ.lf, lag, lunit);
      else if (type === 'SF' && succ.lf) {
        candidate = cal.finishFromStart(cal.applyLag(succ.lf, lag, lunit), duration, unit);
      }
      if (candidate) lf = earlierOf(lf, candidate);
    }

    // Nothing inside a summary can run past the summary itself.
    if (t.parent_task_id) {
      const parentLate = late.get(t.parent_task_id);
      if (parentLate?.lf) lf = earlierOf(lf, parentLate.lf);
    }

    if (!lf) lf = planFinish;
    const ls = cal.startFromFinish(lf, duration, unit);
    late.set(id, { ls, lf });

    const float = cal.workingDaysBetween(t.end_date, lf);
    t.total_float_days = float === null ? null : float - 1;
    t.late_start = ls;
    t.late_finish = lf;
    t.is_critical = t.total_float_days !== null && t.total_float_days <= 0 ? 1 : 0;
  }

  // A summary takes the float of the tightest thing inside it, and is critical
  // when it contains something critical. Scoring a summary on its own rolled-up
  // dates would light up every parent in the plan. Deepest first, so a summary
  // of summaries sees its children already resolved.
  const depthOf = (t) => {
    let d = 0, cur = t, guard = 0;
    while (cur?.parent_task_id && guard++ < 1000) { cur = byId.get(cur.parent_task_id); d++; }
    return d;
  };
  const summaries = rows.filter(t => (childrenOf.get(t.id) || []).length);
  summaries.sort((a, b) => depthOf(b) - depthOf(a));
  for (const t of summaries) {
    const kids = childrenOf.get(t.id);
    const floats = kids.map(k => k.total_float_days).filter(v => v !== null && v !== undefined);
    t.total_float_days = floats.length ? Math.min(...floats) : null;
    t.is_critical = kids.some(k => k.is_critical) ? 1 : 0;
  }
}

/* ────────────────────────────────────────────────────────────
   WBS numbering
   ──────────────────────────────────────────────────────────── */

/**
 * Assign 1, 1.1, 1.1.2 … in the order the plan is laid out, and return the
 * rows flattened depth-first with `wbs_code`, `depth` and `child_count` set.
 * That order is the one both the table and the chart render in, so they can
 * no longer disagree.
 */
export function buildWbs(tasks) {
  const rows = tasks.map(t => ({ ...t }));
  const byId = new Map(rows.map(t => [t.id, t]));
  const children = new Map(rows.map(t => [t.id, []]));
  const roots = [];

  for (const t of rows) {
    const parent = t.parent_task_id && byId.get(t.parent_task_id);
    // A parent that points outside this plan is treated as no parent, rather
    // than losing the task off the bottom of the chart.
    if (parent && parent.id !== t.id) children.get(parent.id).push(t);
    else roots.push(t);
  }

  const bySort = (a, b) =>
    (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
    String(a.created_at || '').localeCompare(String(b.created_at || '')) ||
    String(a.id).localeCompare(String(b.id));

  roots.sort(bySort);
  for (const list of children.values()) list.sort(bySort);

  const flat = [];
  const walk = (node, prefix, depth) => {
    node.wbs_code = prefix;
    node.depth = depth;
    node.child_count = children.get(node.id).length;
    node.has_children = node.child_count > 0;
    flat.push(node);
    children.get(node.id).forEach((kid, i) => walk(kid, `${prefix}.${i + 1}`, depth + 1));
  };
  roots.forEach((r, i) => walk(r, String(i + 1), 0));

  // Anything unreachable (a parent chain that loops) still has to be shown.
  if (flat.length !== rows.length) {
    const seen = new Set(flat.map(t => t.id));
    for (const t of rows) {
      if (seen.has(t.id)) continue;
      t.wbs_code = '?';
      t.depth = 0;
      t.child_count = 0;
      t.has_children = false;
      flat.push(t);
    }
  }
  return flat;
}

/* ────────────────────────────────────────────────────────────
   Progress and variance — "are we ahead or behind"
   ──────────────────────────────────────────────────────────── */

/**
 * Compare where a task should be against where it says it is.
 *
 * expected_pct is straight-line across the task's own working days, which is
 * the assumption every Gantt makes and is honest enough at task level.
 */
export function taskProgress(task, cal, today) {
  const pct = effectivePct(task);
  const out = {
    completion_pct: pct,
    expected_pct: null,
    variance_pct: null,
    schedule_state: 'not_started',
    days_late: null,
  };
  if (!task.start_date || !task.end_date) return out;

  const unit = task.duration_unit === 'calendar' ? 'calendar' : 'workdays';
  const span = unit === 'calendar'
    ? (calendarDaysBetween(task.start_date, task.end_date) ?? 0) + 1
    : cal.workingDaysBetween(task.start_date, task.end_date);

  let expected;
  if (toUTC(today) < toUTC(task.start_date)) expected = 0;
  else if (toUTC(today) > toUTC(task.end_date)) expected = 100;
  else {
    const done = unit === 'calendar'
      ? (calendarDaysBetween(task.start_date, today) ?? 0) + 1
      : cal.workingDaysBetween(task.start_date, today);
    expected = span > 0 ? Math.round((done / span) * 100) : 100;
  }

  out.expected_pct = Math.min(100, Math.max(0, expected));
  out.variance_pct = pct - out.expected_pct;

  if (pct >= 100) out.schedule_state = 'complete';
  else if (task.status === 'blocked') out.schedule_state = 'blocked';
  else if (out.expected_pct === 0) out.schedule_state = 'not_started';
  else if (out.variance_pct >= 5) out.schedule_state = 'ahead';
  else if (out.variance_pct <= -5) out.schedule_state = 'behind';
  else out.schedule_state = 'on_track';

  // An unfinished task whose finish is in the past is late by working days,
  // which is the number a PM actually reports.
  if (pct < 100 && toUTC(today) > toUTC(task.end_date)) {
    out.days_late = (cal.workingDaysBetween(task.end_date, today) || 1) - 1;
  }
  return out;
}

/** Signed slip against the current baseline, in working days. Positive is late. */
export function baselineVariance(task, cal) {
  const out = { start_variance_days: null, finish_variance_days: null };
  if (task.baseline_start && task.start_date) {
    const v = cal.workingDaysBetween(task.baseline_start, task.start_date);
    out.start_variance_days = v === null ? null : (v > 0 ? v - 1 : v + 1);
  }
  if (task.baseline_finish && task.end_date) {
    const v = cal.workingDaysBetween(task.baseline_finish, task.end_date);
    out.finish_variance_days = v === null ? null : (v > 0 ? v - 1 : v + 1);
  }
  return out;
}

/* ────────────────────────────────────────────────────────────
   Validation — refuse a plan that cannot mean anything
   ──────────────────────────────────────────────────────────── */

/**
 * Would adding this link create a cycle, through dependencies or through the
 * WBS? Called before a link is written so the plan can never reach a state the
 * engine will refuse to schedule.
 */
export function wouldCreateCycle({ tasks, deps, newDep }) {
  const candidate = [...deps, newDep];
  try {
    topoOrder(tasks, candidate);
    return false;
  } catch (e) {
    if (e instanceof ScheduleCycleError) return true;
    throw e;
  }
}

/** Is `candidateParentId` inside `taskId`'s own subtree? Blocks self-parenting. */
export function isDescendant(tasks, taskId, candidateParentId) {
  const byId = new Map(tasks.map(t => [t.id, t]));
  let cur = byId.get(candidateParentId);
  let guard = 0;
  while (cur && guard++ < 1000) {
    if (cur.id === taskId) return true;
    cur = cur.parent_task_id ? byId.get(cur.parent_task_id) : null;
  }
  return false;
}
